(() => {
  'use strict';

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ParticleLandscape {
    constructor(canvas, options = {}) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d', { alpha: true });
      this.host = options.host || canvas.parentElement;
      this.colors = this.parseColors(options.colors || canvas.dataset.colors || '#B9FF45,#FF3EA5,#FFFFFF,#8A6CFF,#32F5C8');
      this.density = clamp(Number(options.density || canvas.dataset.density || 80), 20, 100) / 100;
      this.pointer = { x: 0, y: 0, tx: 0, ty: 0 };
      this.points = [];
      this.ribbons = [];
      this.signals = [];
      this.running = false;
      this.inView = true;
      this.time = Math.random() * 100;
      this.raf = 0;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.boundResize = this.resize.bind(this);
      this.boundPointer = this.onPointerMove.bind(this);
      this.init();
    }

    parseColors(value) {
      const colors = String(value).split(',').map((color) => color.trim()).filter(Boolean);
      return colors.length ? colors : ['#B9FF45', '#FF3EA5', '#FFFFFF', '#8A6CFF', '#32F5C8'];
    }

    init() {
      this.resize();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this.host);
      } else {
        window.addEventListener('resize', this.boundResize, { passive: true });
      }

      this.host.addEventListener('pointermove', this.boundPointer, { passive: true });
      this.host.addEventListener('pointerleave', () => {
        this.pointer.tx = 0;
        this.pointer.ty = 0;
      }, { passive: true });

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.inView = entries[0]?.isIntersecting !== false;
          if (this.inView && !this.running) {
            this.start();
          } else if (!this.inView && this.running) {
            this.running = false;
            cancelAnimationFrame(this.raf);
          }
        }, { rootMargin: '220px' });
        this.intersectionObserver.observe(this.canvas);
      }

      this.start();
    }

    resize() {
      const rect = this.host.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      const dpr = Math.min(window.devicePixelRatio || 1, 1.7);
      this.width = width;
      this.height = height;
      this.dpr = dpr;
      this.horizon = height * (width < 700 ? 0.52 : 0.46);
      this.canvas.width = Math.round(width * dpr);
      this.canvas.height = Math.round(height * dpr);
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.buildScene();
      if (prefersReducedMotion) this.render(0);
    }

    buildScene() {
      const area = this.width * this.height;
      const count = Math.round(clamp(area / 540, 900, 3900) * this.density);
      const seed = Math.random() * Math.PI * 2;
      this.points = Array.from({ length: count }, (_, index) => {
        const depth = Math.pow(Math.random(), 0.58);
        const spread = 0.14 + depth * 1.18;
        const lane = (Math.random() - 0.5) * 2 * spread;
        const horizonBias = Math.pow(depth, 1.9);
        return {
          lane,
          depth,
          horizonBias,
          phase: Math.random() * Math.PI * 2 + seed,
          speed: 0.16 + Math.random() * 0.52,
          size: 0.42 + Math.random() * 2.4,
          color: this.colors[index % this.colors.length],
          alpha: 0.18 + Math.random() * 0.72,
          altitude: (Math.random() - 0.5) * 0.18,
          drift: (Math.random() - 0.5) * 0.022,
          twinkle: Math.random() * 0.8 + 0.2
        };
      }).sort((a, b) => a.depth - b.depth);

      const ribbonCount = Math.round(36 * this.density);
      this.ribbons = Array.from({ length: ribbonCount }, (_, index) => ({
        y: 0.48 + Math.random() * 0.34,
        amplitude: 0.015 + Math.random() * 0.06,
        frequency: 1.2 + Math.random() * 4.8,
        phase: Math.random() * Math.PI * 2,
        color: this.colors[index % this.colors.length],
        alpha: 0.035 + Math.random() * 0.08,
        width: 0.45 + Math.random() * 1.15,
        tilt: (Math.random() - 0.5) * 0.3
      }));

      const signalCount = Math.max(8, Math.round(15 * this.density));
      this.signals = Array.from({ length: signalCount }, (_, index) => ({
        progress: Math.random(),
        speed: 0.025 + Math.random() * 0.075,
        lane: (Math.random() - 0.5) * 1.4,
        length: 80 + Math.random() * 180,
        color: this.colors[index % this.colors.length],
        alpha: 0.18 + Math.random() * 0.32,
        offset: Math.random() * Math.PI * 2
      }));
    }

    onPointerMove(event) {
      const rect = this.host.getBoundingClientRect();
      this.pointer.tx = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      this.pointer.ty = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.last = performance.now();
      const loop = (now) => {
        if (!this.running) return;
        const delta = Math.min(40, now - this.last);
        this.last = now;
        if (this.inView) this.render(delta);
        if (prefersReducedMotion) {
          this.running = false;
          return;
        }
        this.raf = requestAnimationFrame(loop);
      };
      this.raf = requestAnimationFrame(loop);
    }

    render(delta) {
      const ctx = this.ctx;
      const width = this.width;
      const height = this.height;
      const horizon = this.horizon;
      this.time += delta * 0.00026;
      this.pointer.x += (this.pointer.tx - this.pointer.x) * 0.045;
      this.pointer.y += (this.pointer.ty - this.pointer.y) * 0.045;
      ctx.clearRect(0, 0, width, height);

      this.drawAtmosphere(ctx, width, height, horizon);
      this.drawRibbons(ctx, width, height, horizon);
      this.drawTerrain(ctx, width, height, horizon);
      this.drawSignals(ctx, width, height, horizon, delta);
      this.drawVignette(ctx, width, height);
    }

    drawAtmosphere(ctx, width, height, horizon) {
      const centerX = width * (0.52 + this.pointer.x * 0.025);
      const glow = ctx.createRadialGradient(centerX, horizon * 0.62, 0, width * 0.5, horizon * 0.75, Math.max(width, height) * 0.72);
      glow.addColorStop(0, 'rgba(255,255,255,.30)');
      glow.addColorStop(0.3, this.hexToRgba(this.colors[1] || '#FF3EA5', 0.11));
      glow.addColorStop(0.58, this.hexToRgba(this.colors[0] || '#B9FF45', 0.09));
      glow.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, width, height);

      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      for (let i = 0; i < 6; i += 1) {
        ctx.beginPath();
        const radiusX = width * (0.28 + i * 0.07);
        const radiusY = height * (0.055 + i * 0.016);
        const y = horizon + i * height * 0.028 + Math.sin(this.time * 0.8 + i) * 8;
        ctx.ellipse(width * (0.5 + this.pointer.x * 0.015), y, radiusX, radiusY, 0, Math.PI * 1.02, Math.PI * 1.98);
        ctx.strokeStyle = this.hexToRgba(this.colors[i % this.colors.length], 0.08 + i * 0.012);
        ctx.lineWidth = 1 + i * 0.4;
        ctx.stroke();
      }
      ctx.restore();
    }

    drawRibbons(ctx, width, height, horizon) {
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      this.ribbons.forEach((ribbon) => {
        ctx.beginPath();
        for (let x = -30; x <= width + 30; x += 20) {
          const u = x / width;
          const perspectiveY = height * ribbon.y;
          const y = perspectiveY + Math.sin(u * Math.PI * 2 * ribbon.frequency + ribbon.phase + this.time * (0.7 + ribbon.tilt)) * ribbon.amplitude * height + this.pointer.y * 12;
          const skewX = x + Math.sin(this.time + ribbon.phase) * ribbon.tilt * 28 + this.pointer.x * 18;
          if (x === -30) ctx.moveTo(skewX, y); else ctx.lineTo(skewX, y);
        }
        ctx.strokeStyle = this.hexToRgba(ribbon.color, ribbon.alpha);
        ctx.lineWidth = ribbon.width;
        ctx.stroke();
      });
      ctx.restore();
    }

    drawTerrain(ctx, width, height, horizon) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      this.points.forEach((point) => {
        const depth = point.depth;
        const perspective = 0.16 + depth * 1.45;
        const wave = Math.sin(this.time * point.speed + point.phase + point.lane * 2.2) * 0.018;
        const pulse = 0.72 + Math.sin(this.time * 2.2 + point.phase) * 0.28 * point.twinkle;
        const tunnel = Math.pow(Math.abs(point.lane), 1.6) * 0.13;
        const x = width * (0.5 + point.lane * (0.19 + depth * 0.39) + Math.sin(this.time * 0.4 + point.phase) * point.drift) + this.pointer.x * (12 + depth * 46);
        const y = horizon + depth * depth * height * 0.5 + (tunnel + wave + point.altitude * depth) * height + this.pointer.y * (8 + depth * 22);
        if (x < -30 || x > width + 30 || y < -30 || y > height + 35) return;
        const size = point.size * perspective * (width < 700 ? 0.72 : 1);
        const alpha = point.alpha * (0.18 + depth * 0.92) * pulse;
        ctx.beginPath();
        ctx.ellipse(x, y, size * 1.55, Math.max(0.35, size * 0.55), point.lane * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = this.hexToRgba(point.color, alpha);
        ctx.fill();
      });
      ctx.restore();
    }

    drawSignals(ctx, width, height, horizon, delta) {
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      this.signals.forEach((signal) => {
        signal.progress = (signal.progress + signal.speed * (delta / 16.67) * 0.01) % 1;
        const p = signal.progress;
        const depth = Math.pow(p, 1.25);
        const x = width * (0.5 + signal.lane * (0.08 + depth * 0.43)) + this.pointer.x * 22;
        const y = horizon + depth * depth * height * 0.48 + Math.sin(this.time + signal.offset) * 12;
        const endX = x - signal.length * (0.32 + depth) * (signal.lane >= 0 ? 1 : -1);
        const endY = y - signal.length * 0.32;
        const gradient = ctx.createLinearGradient(endX, endY, x, y);
        gradient.addColorStop(0, this.hexToRgba(signal.color, 0));
        gradient.addColorStop(1, this.hexToRgba(signal.color, signal.alpha));
        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(x, y);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 0.8 + depth * 1.8;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y, 1.2 + depth * 2.4, 0, Math.PI * 2);
        ctx.fillStyle = this.hexToRgba(signal.color, signal.alpha + 0.15);
        ctx.fill();
      });
      ctx.restore();
    }

    drawVignette(ctx, width, height) {
      const haze = ctx.createLinearGradient(0, height * 0.42, 0, height);
      haze.addColorStop(0, 'rgba(255,255,255,0)');
      haze.addColorStop(0.68, 'rgba(8,7,12,.05)');
      haze.addColorStop(1, 'rgba(5,5,8,.54)');
      ctx.fillStyle = haze;
      ctx.fillRect(0, 0, width, height);
    }

    hexToRgba(color, alpha) {
      if (!color || !color.startsWith('#')) return color || `rgba(255,255,255,${alpha})`;
      let hex = color.slice(1);
      if (hex.length === 3) hex = hex.split('').map((char) => char + char).join('');
      const number = Number.parseInt(hex, 16);
      if (Number.isNaN(number)) return `rgba(255,255,255,${alpha})`;
      const red = (number >> 16) & 255;
      const green = (number >> 8) & 255;
      const blue = number & 255;
      return `rgba(${red},${green},${blue},${alpha})`;
    }

    destroy() {
      this.running = false;
      cancelAnimationFrame(this.raf);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      window.removeEventListener('resize', this.boundResize);
      this.host.removeEventListener('pointermove', this.boundPointer);
    }
  }

  const instances = new WeakMap();

  function initializeParticleFields(root = document) {
    root.querySelectorAll('[data-particle-hero]').forEach((hero) => {
      const canvas = hero.querySelector('[data-particle-canvas]');
      if (!canvas || instances.has(canvas)) return;
      instances.set(canvas, new ParticleLandscape(canvas, {
        host: hero,
        density: hero.dataset.particleDensity,
        colors: hero.dataset.particleColors
      }));
    });

    root.querySelectorAll('canvas[data-particle-field]').forEach((canvas) => {
      if (instances.has(canvas)) return;
      instances.set(canvas, new ParticleLandscape(canvas, {
        host: canvas.parentElement,
        density: canvas.dataset.density,
        colors: canvas.dataset.colors
      }));
    });
  }

  document.addEventListener('DOMContentLoaded', () => initializeParticleFields());
  document.addEventListener('shopify:section:load', (event) => initializeParticleFields(event.target));
  window.MediaKingsParticles = { initialize: initializeParticleFields };
})();
